import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot } from "@fortawesome/free-solid-svg-icons";
import "aos/dist/aos.css";
import AOS from "aos";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowUpLeft } from "react-icons/fi";
import { scrollToTop } from "../../utils/navigation";

export default function FeaturesPage() {
  const featuresRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  const handleSee = () => {
    const offsetTop = featuresRef.current?.offsetTop;
    window.scrollTo({
      top: offsetTop - 100,
      behavior: "smooth",
    });
  };

  const handleClick = () => {
    scrollToTop();
    navigate("/signup");
  };

  return (
    <>
      <main className="relative overflow-hidden font-sans">
        <section className="relative flex items-center justify-center h-screen px-4 text-center text-white bg-gradient-to-b from-blue-600 to-green-500 sm:px-8">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-64 h-64 rounded-full md:w-96 md:h-96 bg-gradient-to-r from-blue-400 to-green-400 opacity-30 blur-lg animate-pulse" />
          </div>

          <div
            className="relative z-10 space-y-6 transition duration-500 ease-in-out transform"
            data-aos="fade-up">
            <h1 className="max-w-4xl text-3xl font-bold leading-tight sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl animate-text-glow">
              AI-Powered.
              <br /> Human-Driven.
              <br /> Accounting Evolved.
            </h1>
            <p
              className="max-w-2xl mx-auto text-sm font-light sm:text-lg md:text-xl lg:text-2xl opacity-90"
              data-aos="zoom-in"
              data-aos-delay="300">
              Over 3000 Questions Answered Instantly.
            </p>
            <button
              onClick={handleSee}
              className="px-6 py-3 mt-8 font-semibold text-blue-500 transition duration-300 ease-in-out transform bg-white rounded-full shadow-lg sm:px-8 sm:py-4 hover:scale-105 hover:shadow-2xl"
              data-aos="fade-up"
              data-aos-delay="600">
              See More
            </button>
          </div>

          <div
            className="absolute text-3xl text-white transition duration-500 ease-in-out transform bottom-6 right-6 sm:bottom-10 sm:right-10 md:bottom-12 md:right-12 sm:text-4xl md:text-5xl opacity-80 hover:scale-110"
            data-aos="fade-left"
            data-aos-delay="800">
            <FontAwesomeIcon
              icon={faRobot}
              className="animate-spin-slow"
            />
          </div>
        </section>

        <section
          ref={featuresRef}
          className="flex flex-col items-center justify-center min-h-screen px-6 py-10 space-y-10 text-white bg-gray-200 sm:px-8">
          <div
            className="flex flex-col items-center w-full max-w-5xl space-y-8 transition duration-500 ease-in-out transform lg:flex-row lg:space-y-0 lg:space-x-12"
            data-aos="fade-right">
            <div className="w-full transition duration-300 transform lg:w-5/12 hover:scale-105">
              <img
                src="/rsrch.jpg"
                className="object-cover w-full h-80 lg:h-[22rem] rounded-lg shadow-2xl"
                alt="Research Background"
              />
            </div>

            <div
              className="w-full p-6 space-y-6 transition-transform duration-500 ease-in-out transform bg-black rounded-lg shadow-xl lg:w-7/12 backdrop-blur-lg bg-opacity-70 sm:p-10 hover:scale-105"
              data-aos="fade-left">
              <h2 className="text-3xl font-semibold tracking-wide text-center sm:text-4xl">
                Instant Research
              </h2>
              <p className="max-w-xl mx-auto leading-relaxed text-center">
                Ask for solutions for Tax, Accounting, Audit, and Licensing in
                seconds.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={(e) => {
                    navigate("/features/instant-research");
                    window.scrollTo(0, 0);
                  }}
                  className="w-full px-6 py-2 mt-4 font-semibold transition duration-300 ease-in-out transform bg-white rounded-md shadow-lg text-customText-900 sm:mt-6 sm:px-8 hover:scale-105 hover:shadow-2xl">
                  Learn More
                </button>
              </div>
            </div>
          </div>

          <div
            className="flex flex-col-reverse items-center w-full max-w-5xl transition duration-500 ease-in-out transform lg:flex-row lg:space-x-12"
            data-aos="fade-left">
            <div
              className="w-full p-6 space-y-6 transition-transform duration-500 ease-in-out transform bg-black rounded-lg shadow-xl lg:w-7/12 backdrop-blur-lg bg-opacity-70 sm:p-10 hover:scale-105"
              data-aos="fade-right">
              <h2 className="text-3xl font-semibold tracking-wide text-center sm:text-4xl">
                Quick Email Response
              </h2>
              <p className="max-w-xl mx-auto leading-relaxed text-center">
                Provide a brief prompt and let B.E.T.H. draft a well-crafted
                email, whether for a business proposal, business pitch, or
                response to a client inquiry.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={(e) => {
                    navigate("/features/email-response");
                    window.scrollTo(0, 0);
                  }}
                  className="w-full px-6 py-2 mt-4 font-semibold transition duration-300 ease-in-out transform bg-white rounded-md shadow-lg text-customText-900 sm:mt-6 sm:px-8 hover:scale-105 hover:shadow-2xl">
                  Learn More
                </button>
              </div>
            </div>

            <div className="w-full mb-8 transition duration-300 transform lg:w-5/12 hover:scale-105 sm:mb:0">
              <img
                src="/qer.jpg"
                className="object-cover w-full h-80 lg:h-[22rem] rounded-lg shadow-2xl"
                alt="Email Response"
              />
            </div>
          </div>

          <div
            className="flex flex-col items-center w-full max-w-5xl space-y-8 transition duration-500 ease-in-out transform lg:flex-row lg:space-y-0 lg:space-x-12"
            data-aos="fade-right">
            <div className="w-full transition duration-300 transform lg:w-5/12 hover:scale-105">
              <img
                src="/rsrch.jpg"
                className="object-cover w-full h-80 lg:h-[22rem] rounded-lg shadow-2xl"
                alt="Research Background"
              />
            </div>

            <div
              className="w-full p-6 space-y-6 transition-transform duration-500 ease-in-out transform bg-black rounded-lg shadow-xl lg:w-7/12 backdrop-blur-lg bg-opacity-70 sm:p-10 hover:scale-105"
              data-aos="fade-left">
              <h2 className="text-3xl font-semibold tracking-wide text-center sm:text-4xl">
                Electric Cooperative
              </h2>
              <p className="max-w-xl mx-auto leading-relaxed text-center">
                Smarter Compliance, Stronger Cooperatives, Powered by AI.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={(e) => {
                    navigate("/features/electric-cooperative");
                    window.scrollTo(0, 0);
                  }}
                  className="w-full py-2 mt-2.5 font-semibold text-customText-900 transition duration-300 ease-in-out transform bg-white rounded-md shadow-lg sm:mt-6 sm:px-8 hover:scale-105 hover:shadow-2xl">
                  Learn More
                </button>
              </div>
            </div>
          </div>

          <div
            className="flex flex-col items-center w-full max-w-5xl space-y-8 transition duration-500 ease-in-out transform lg:flex-row lg:space-y-0 lg:space-x-12"
            data-aos="fade-right">
            <div
              className="w-full p-6 space-y-6 transition-transform duration-500 ease-in-out transform bg-black rounded-lg shadow-xl lg:w-7/12 backdrop-blur-lg bg-opacity-70 sm:p-10 hover:scale-105"
              data-aos="fade-left">
              <h2 className="text-3xl font-semibold tracking-wide text-center sm:text-4xl">
                Invoicing Checker Bot
              </h2>
              <p className="max-w-xl mx-auto leading-relaxed text-center">
                Effortless Validation, Hassle-Free Invoicing, Analyzed and
                Reviewed by AI.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={(e) => {
                    navigate("/features/invoicing-checker");
                    window.scrollTo(0, 0);
                  }}
                  className="w-full py-2 mt-2.5 font-semibold text-customText-900 transition duration-300 ease-in-out transform bg-white rounded-md shadow-lg sm:mt-6 sm:px-8 hover:scale-105 hover:shadow-2xl">
                  Learn More
                </button>
              </div>
            </div>
            <div className="w-full transition duration-300 transform lg:w-5/12 hover:scale-105">
              <img
                src="/rsrch.jpg"
                className="object-cover w-full h-80 lg:h-[22rem] rounded-lg shadow-2xl"
                alt="Research Background"
              />
            </div>
          </div>
        </section>

        <section className="relative flex items-center justify-center h-screen p-4 text-center text-white bg-gradient-to-r from-blue-500 to-green-400 sm:p-10">
          <div
            className="space-y-8 transition duration-500 ease-in-out transform hover:scale-105"
            data-aos="fade-up">
            <h2 className="text-2xl font-semibold sm:text-3xl md:text-4xl">
              Ready to Transform Your Accounting?
            </h2>
            <p className="max-w-xl mx-auto font-light text-md sm:text-lg opacity-90">
              Discover the full potential of intelligent finance solutions with
              seamless integration and enhanced security.
            </p>
            <button
              onClick={() => handleClick()}
              className="px-8 py-3 font-semibold text-blue-500 transition duration-300 ease-in-out transform bg-white rounded-full shadow-xl sm:px-10 sm:py-4 hover:bg-gray-100 hover:scale-105">
              Get Started
            </button>
          </div>
        </section>
        <aside className="fixed left-6 bottom-6">
          <Link
            to="/"
            onClick={scrollToTop}
            className="flex items-center p-4 space-x-2 transition duration-300 bg-white rounded-lg shadow-lg hover:bg-gray-100">
            <FiArrowUpLeft className="text-gray-600" />
            <span className="text-gray-600">Back to Home</span>
          </Link>
        </aside>
      </main>
    </>
  );
}
